"use client";

import * as React from "react";
import type { Environment } from "../../../types";
import type { TokenPriceInterval, TokenPricePoint } from "../types";
import { useTokenPriceHistory } from "../queries/use-token-price-history";
import { formatPrice } from "../../../lib/format";

export interface TokenPriceChartProps {
  env: Environment;
  chainId: number;
  tokenAddress: string;
}

const UP_COLOR = "var(--ds-system-green-high, #00a869)";
const DOWN_COLOR = "var(--wi-error, #e70077)";

const CHART_HEIGHT = 150;
const PAD_X = 3;
const PAD_TOP = 18;
const PAD_BOTTOM = 18;

/** raw interval → 표시 라벨. 순서가 탭 순서를 결정. */
const INTERVALS: ReadonlyArray<{ key: TokenPriceInterval; label: string }> = [
  { key: "10m", label: "10m" },
  { key: "1h", label: "1H" },
  { key: "1d", label: "1D" },
  { key: "7d", label: "1W" },
  { key: "30d", label: "1M" },
  { key: "1y", label: "1Y" },
];

const LONG_RANGE: ReadonlySet<TokenPriceInterval> = new Set([
  "7d",
  "30d",
  "1y",
]);

function useElementWidth() {
  const ref = React.useRef<HTMLDivElement>(null);
  const [width, setWidth] = React.useState(0);
  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      setWidth(entries[0]?.contentRect.width ?? 0);
    });
    ro.observe(el);
    setWidth(el.clientWidth);
    return () => ro.disconnect();
  }, []);
  return [ref, width] as const;
}

/** unix초 → 로컬 시간 라벨. 장기 구간은 날짜까지 표기. */
function formatPointTime(unixSec: number, longRange: boolean): string {
  const d = new Date(unixSec * 1000);
  const opts: Intl.DateTimeFormatOptions = longRange
    ? { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }
    : { hour: "numeric", minute: "2-digit" };
  return new Intl.DateTimeFormat("en-US", opts).format(d);
}

interface Geometry {
  values: number[];
  points: Array<{ x: number; y: number }>;
  linePath: string;
  areaPath: string;
  maxIdx: number;
  minIdx: number;
}

function buildGeometry(values: number[], width: number): Geometry | null {
  if (width <= 0 || values.length === 0) return null;

  // 0/1 포인트는 평평한 2점으로 패딩한다.
  const vals = values.length === 1 ? [values[0], values[0]] : values;
  const n = vals.length;

  let max = vals[0];
  let min = vals[0];
  let maxIdx = 0;
  let minIdx = 0;
  for (let i = 1; i < n; i++) {
    if (vals[i] > max) {
      max = vals[i];
      maxIdx = i;
    }
    if (vals[i] < min) {
      min = vals[i];
      minIdx = i;
    }
  }
  const range = max - min || 1;
  const innerW = Math.max(1, width - PAD_X * 2);
  const innerH = CHART_HEIGHT - PAD_TOP - PAD_BOTTOM;

  const xFor = (i: number) =>
    n === 1 ? width / 2 : PAD_X + (i / (n - 1)) * innerW;
  const yFor = (v: number) => PAD_TOP + (1 - (v - min) / range) * innerH;

  const points = vals.map((v, i) => ({ x: xFor(i), y: yFor(v) }));

  const linePath = points
    .map((p, i) => `${i === 0 ? "M" : "L"}${p.x.toFixed(2)},${p.y.toFixed(2)}`)
    .join(" ");

  const first = points[0];
  const last = points[points.length - 1];
  const areaPath = `${linePath} L${last.x.toFixed(2)},${CHART_HEIGHT} L${first.x.toFixed(2)},${CHART_HEIGHT} Z`;

  return { values: vals, points, linePath, areaPath, maxIdx, minIdx };
}

export function TokenPriceChart({
  env,
  chainId,
  tokenAddress,
}: TokenPriceChartProps) {
  const [interval, setInterval] = React.useState<TokenPriceInterval>("1d");
  const [hoverIdx, setHoverIdx] = React.useState<number | null>(null);
  const gradientId = React.useId();
  const [plotRef, width] = useElementWidth();

  const { data, isLoading, isError } = useTokenPriceHistory(
    env,
    chainId,
    tokenAddress,
    interval,
    true,
  );

  // 시간순 정렬 + 유효 숫자만.
  const series = React.useMemo(() => {
    const rows: TokenPricePoint[] = (data ?? [])
      .filter((p) => Number.isFinite(Number(p.price)))
      .slice()
      .sort((a, b) => a.updated_at - b.updated_at);
    return rows;
  }, [data]);

  const values = React.useMemo(() => series.map((p) => Number(p.price)), [series]);
  const geometry = React.useMemo(
    () => buildGeometry(values, width),
    [values, width],
  );

  // interval 이 바뀌면 hover 해제.
  React.useEffect(() => {
    setHoverIdx(null);
  }, [interval]);

  const hasData = series.length > 0;
  const currentIdx =
    hoverIdx !== null && hoverIdx < values.length
      ? hoverIdx
      : values.length - 1;
  const currentValue = hasData ? values[currentIdx] : 0;
  const firstValue = hasData ? values[0] : 0;
  const changePct =
    firstValue !== 0 ? ((currentValue - firstValue) / firstValue) * 100 : 0;
  const isUp = changePct >= 0;
  const color = isUp ? UP_COLOR : DOWN_COLOR;

  const changeText =
    (isUp ? "+" : "-") +
    (Math.floor(Math.abs(changePct) * 100) / 100).toFixed(2) +
    "%";

  const whenText =
    hoverIdx !== null && series[hoverIdx]
      ? formatPointTime(series[hoverIdx].updated_at, LONG_RANGE.has(interval))
      : "";

  const handlePointerMove = (e: React.PointerEvent<SVGRectElement>) => {
    if (!geometry || width <= 0) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const relX = e.clientX - rect.left;
    const n = geometry.values.length;
    if (n <= 1) {
      setHoverIdx(0);
      return;
    }
    const innerW = Math.max(1, width - PAD_X * 2);
    const ratio = Math.min(1, Math.max(0, (relX - PAD_X) / innerW));
    // series 길이 기준으로 인덱스를 매핑(패딩된 geometry가 아니라 실제 series).
    const idx = Math.round(ratio * (series.length - 1));
    setHoverIdx(Math.min(series.length - 1, Math.max(0, idx)));
  };

  const clearHover = () => setHoverIdx(null);

  // 데이터 없음/에러 시 차트 섹션 자체를 접는다(레이아웃 점프 최소화 위해 로딩만 스켈레톤).
  if (!isLoading && (isError || !hasData)) return null;

  const hoverPoint =
    hoverIdx !== null && geometry && geometry.points[hoverIdx]
      ? geometry.points[hoverIdx]
      : null;

  const maxPoint = geometry?.points[geometry.maxIdx];
  const minPoint = geometry?.points[geometry.minIdx];
  const clampLabelX = (x: number) =>
    Math.min(Math.max(x, 24), Math.max(24, width - 24));

  return (
    <section className="wi-td-chart">
      <div className="wi-td-chart-panel">
        <span className="wi-td-chart-price">
          {hasData ? formatPrice(currentValue) : "—"}
        </span>
        {hasData && (
          <span className="wi-td-chart-change" data-positive={isUp}>
            <span>{changeText}</span>
            {whenText && <span className="wi-td-chart-when">{whenText}</span>}
          </span>
        )}
      </div>

      <div ref={plotRef} className="wi-td-chart-plot">
        {isLoading && !hasData ? (
          <div className="wi-skeleton-block wi-td-chart-skeleton" />
        ) : (
          geometry &&
          width > 0 && (
            <svg
              className="wi-td-chart-svg"
              width={width}
              height={CHART_HEIGHT}
              role="img"
              aria-label="Price chart"
            >
              <defs>
                <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={color} stopOpacity={0.28} />
                  <stop offset="100%" stopColor={color} stopOpacity={0} />
                </linearGradient>
              </defs>

              <path d={geometry.areaPath} fill={`url(#${gradientId})`} />
              <path
                d={geometry.linePath}
                fill="none"
                stroke={color}
                strokeWidth={2}
                strokeLinejoin="round"
                strokeLinecap="round"
              />

              {maxPoint && (
                <text
                  className="wi-td-chart-minmax"
                  x={clampLabelX(maxPoint.x)}
                  y={Math.max(11, maxPoint.y - 7)}
                  textAnchor="middle"
                >
                  {formatPrice(geometry.values[geometry.maxIdx])}
                </text>
              )}
              {minPoint && geometry.minIdx !== geometry.maxIdx && (
                <text
                  className="wi-td-chart-minmax"
                  x={clampLabelX(minPoint.x)}
                  y={Math.min(CHART_HEIGHT - 5, minPoint.y + 14)}
                  textAnchor="middle"
                >
                  {formatPrice(geometry.values[geometry.minIdx])}
                </text>
              )}

              {hoverPoint && (
                <>
                  <line
                    className="wi-td-chart-cursor"
                    x1={hoverPoint.x}
                    y1={PAD_TOP - 6}
                    x2={hoverPoint.x}
                    y2={CHART_HEIGHT}
                  />
                  <circle
                    cx={hoverPoint.x}
                    cy={hoverPoint.y}
                    r={4}
                    fill={color}
                    stroke="var(--wi-surface-bg, #121212)"
                    strokeWidth={2}
                  />
                </>
              )}

              <rect
                x={0}
                y={0}
                width={width}
                height={CHART_HEIGHT}
                fill="transparent"
                style={{ touchAction: "none" }}
                onPointerMove={handlePointerMove}
                onPointerDown={handlePointerMove}
                onPointerLeave={clearHover}
                onPointerCancel={clearHover}
              />
            </svg>
          )
        )}
      </div>

      <div className="wi-td-chart-tabs" role="tablist">
        {INTERVALS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            role="tab"
            aria-selected={interval === tab.key}
            className="wi-td-chart-tab"
            data-active={interval === tab.key || undefined}
            onClick={() => setInterval(tab.key)}
            data-track="token_detail_interval"
            data-track-id={tab.key}
          >
            {tab.label}
          </button>
        ))}
      </div>
    </section>
  );
}
